import { RootState } from '../';
import { ActionsOf } from '../../helpers/actions';
import { getType, createAction } from 'typesafe-actions';
import URI from 'urijs';
import { keyBy } from 'lodash';
import { produce } from 'immer';
import { takeEvery, select } from 'redux-saga/effects';
import { PathBuilder } from '../../routing';
import { getHistory } from '../../state/history';
import { getSiteContext } from './site';
import { Site, getSiteTags, isChartableTag } from '../../models';
import { isValidDate } from '../../helpers/common';
import { v1 } from 'uuid';

export type Chart = {
  id: string;
  label: string | null;
  tagsIds: string[];
};

export type View = {
  id: string;
  charts: Chart[];
  label: string | null;
};

export interface AnalysisPageContext {
  start?: Date;
  end?: Date;
  views: View[];
}

const DAY_IN_MS = 1000 * 60 * 60 * 24;

/* ----- actions ----- */

export const setStartDate = createAction(
  'context/analysisPage/setStartDate',
  (resolve) => (start: Date) => resolve({ start })
);

export const setEndDate = createAction(
  'context/analysisPage/setEndDeate',
  (resolve) => (end: Date) => resolve({ end })
);

export const setContext = createAction(
  'context/analysisPage/setContext',
  (resolve) => (context: AnalysisPageContext) => resolve({ context })
);

export const updateViews = createAction(
  'context/analysisPage/updateViews',
  (resolve) => (views: View[]) => resolve({ views })
);

export const updateTagsForChartInView = createAction(
  'context/analysisPage/updateTagsForChartInView',
  (resolve) => (viewId: string, chartId: string, tagsIds: string[]) =>
    resolve({ viewId, chartId, tagsIds })
);

export const removeTagFromChartInView = createAction(
  'context/analysisPage/removeTagFromChartInView',
  (resolve) => (viewId: string, chartId: string, tagId: string) =>
    resolve({ viewId, chartId, tagId })
);

export const removeChartInView = createAction(
  'context/analysisPage/removeChartInView',
  (resolve) => (viewId: string, chartId: string) => resolve({ viewId, chartId })
);

export const analysisPageContextActions = {
  setStartDate,
  setEndDate,
  setContext,
  updateViews,
  updateTagsForChartInView,
  removeTagFromChartInView,
  removeChartInView,
};

export const parseAnalysisRouteContext = (_path: string, search: string): AnalysisPageContext => {
  const params = URI.parseQuery(search);
  const context: AnalysisPageContext = { views: [] };
  if (params.startTS && typeof params.startTS === 'string') {
    context.start = new Date(params.startTS);
  }
  if (params.endTS && typeof params.endTS === 'string') {
    context.end = new Date(params.endTS);
  }
  // Handle legacy tagIds
  if (params.tagIds && params.tagIds instanceof Array) {
    context.views.push({
      id: v1(),
      charts: [
        {
          id: v1(),
          tagsIds: params.tagIds.filter((t) => t !== null) as string[],
          label: params.label || '',
        },
      ],
      label: params.label || null,
    });
  } else if (params.tagIds && typeof params.tagIds === 'string') {
    context.views.push({
      id: v1(),
      charts: [
        {
          id: v1(),
          tagsIds: [params.tagIds],
          label: '',
        },
      ],
      label: null,
    });
  }

  // Handle serialized views
  if (params.views && typeof params.views === 'string') {
    try {
      const parsedViews = JSON.parse(params.views as string);
      if (Array.isArray(parsedViews)) {
        context.views = parsedViews.map((view) => ({
          id: view.id || v1(),
          charts: view.charts.map(
            (chart: { id?: string; tagsIds: string | string[]; label?: string }) => ({
              id: chart.id || v1(),
              tagsIds: Array.isArray(chart.tagsIds) ? chart.tagsIds : [chart.tagsIds],
              label: chart.label || '',
            })
          ),
          label: view.label || null,
        }));
      }
    } catch (e) {
      console.error('Error parsing views from URL', e);
    }
  }
  // Handle legacy array format
  else if (params.views && params.views instanceof Array) {
    context.views = params.views.map((view) => ({
      id: view.id || v1(),
      charts: view.charts.map(
        (chart: { id?: string; tagsIds: string | string[]; label?: string }) => ({
          id: chart.id || v1(),
          tagsIds: Array.isArray(chart.tagsIds) ? chart.tagsIds : [chart.tagsIds],
          label: chart.label || '',
        })
      ),
      label: view.label || null,
    }));
  }

  return {
    ...context,
  };
};

const MAX_RANGE = DAY_IN_MS * 7;
export const validateAnalysisContext = (context: AnalysisPageContext, site: Site) => {
  const { start, end, views } = context;
  if (start && !isValidDate(start)) {
    throw new Error('Invalid start date.');
  }
  if (end && !isValidDate(end)) {
    throw new Error('Invalid end date.');
  }
  if (end && start && start >= end) {
    throw new Error('Invalid date range, start date must be before end date.');
  }
  if (end && start && end.getTime() - start.getTime() > MAX_RANGE) {
    throw new Error('Cannot display more than 7 days of data at once.');
  }

  // Note we allow a little padding on the commissionedOn date so the user can
  // see date from the very beginning without hitting it exactly, if they want
  // to for some reason
  if (
    start &&
    site.commissionedOn &&
    new Date(site.commissionedOn).getTime() > start.getTime() - DAY_IN_MS
  ) {
    // If the end date is after the commissioned date, we can adjust the start date
    // Otherwise, throw an error as the requested range is before available data
    if (end && end.getTime() <= new Date(site.commissionedOn).getTime()) {
      throw new Error(
        `Invalid time range, start of available data is ${new Date(
          site.commissionedOn
        ).toLocaleString()}.`
      );
    }
  }
  // Same logic for end time
  if (end && end.getTime() > Date.now() + DAY_IN_MS) {
    throw new Error('Invalid time range, end is past available data.');
  }
  const siteTagLookup = keyBy(getSiteTags(site), (tag) => tag.id);
  views.forEach((view) => {
    view.charts.forEach((chart) => {
      chart.tagsIds.forEach((tagId) => {
        const foundTag = siteTagLookup[tagId];
        if (!foundTag) {
          throw new Error(`No tag found with id ${tagId} for this site.`);
        }
        if (!isChartableTag(foundTag)) {
          throw new Error(`Tag ${tagId} cannot be visualized.`);
        }
      });
    });
  });
};

/* ----- selectors ----- */

export const getAnalysisPageContext = (rootState: RootState) => {
  return rootState.contexts.analysisPage;
};

type AnalysisPageContextActions = ActionsOf<typeof analysisPageContextActions>;

export const analysisPageContextReducer = produce(
  (draft: AnalysisPageContext, action: AnalysisPageContextActions) => {
    switch (action.type) {
      case getType(setStartDate):
        const { start } = action.payload;
        draft.start = start;
        break;
      case getType(setEndDate):
        const { end } = action.payload;
        draft.end = end;
        break;
      case getType(setContext):
        const { context } = action.payload;
        return context;
      case getType(updateViews):
        const { views } = action.payload;
        if (views.length > 0) {
          draft.views = [...draft.views, ...views];
        }
        break;
      case getType(updateTagsForChartInView):
        const {
          viewId: viewIdToUpdate,
          chartId: chartIdToUpdate,
          tagsIds: tagsIdsToUpdate,
        } = action.payload;
        draft.views = draft.views.map((view) =>
          view.id === viewIdToUpdate
            ? {
                ...view,
                charts: view.charts.map((chart) =>
                  chart.id === chartIdToUpdate ? { ...chart, tagsIds: tagsIdsToUpdate } : chart
                ),
              }
            : view
        );

        // if no tags selected and no other charts in the view, remove the view
        if (
          tagsIdsToUpdate.length === 0 &&
          draft.views.some((view) => view.id === viewIdToUpdate && view.charts.length === 1)
        ) {
          draft.views = draft.views.filter((view) => view.id !== viewIdToUpdate);
        }
        break;
      case getType(removeTagFromChartInView):
        const {
          viewId: viewIdToRemoveTagFrom,
          chartId: chartIdToRemoveTagFrom,
          tagId: tagIdToRemove,
        } = action.payload;

        // Update the views and charts with the removed tag
        draft.views = draft.views.map((view) =>
          view.id === viewIdToRemoveTagFrom
            ? {
                ...view,
                charts: view.charts.map((chart) =>
                  chart.id === chartIdToRemoveTagFrom
                    ? {
                        ...chart,
                        tagsIds: chart.tagsIds.filter((tagId) => tagId !== tagIdToRemove),
                      }
                    : chart
                ),
              }
            : view
        );

        // Then clean up: remove charts without tags and views without charts
        draft.views = draft.views
          .map((view) => ({
            ...view,
            charts: view.charts.filter((chart) => chart.tagsIds.length > 0),
          }))
          .filter((view) => view.charts.length > 0);
        break;
      case getType(removeChartInView):
        const { viewId: viewIdToRemoveChartFrom, chartId: chartIdToRemove } = action.payload;
        draft.views = draft.views.map((view) =>
          view.id === viewIdToRemoveChartFrom
            ? { ...view, charts: view.charts.filter((chart) => chart.id !== chartIdToRemove) }
            : view
        );
        draft.views = draft.views.filter((view) => view.charts.length > 0);
        break;
      default:
        break;
    }
    return;
  },
  {
    views: [],
  } as AnalysisPageContext
);

function* handleSetRouteContext() {
  const history = getHistory();
  const site: Site = yield select(getSiteContext);
  const context: AnalysisPageContext = yield select(getAnalysisPageContext);
  if (context.start && context.end) {
    const url = PathBuilder.ANALYSIS_QUERY(site.id, context.views, context.start, context.end);
    history.push(url);
  }
}

export function* analysisContextSagas() {
  yield takeEvery(getType(setStartDate), handleSetRouteContext);
  yield takeEvery(getType(setEndDate), handleSetRouteContext);
  yield takeEvery(getType(updateViews), handleSetRouteContext);
  yield takeEvery(getType(updateTagsForChartInView), handleSetRouteContext);
  yield takeEvery(getType(removeTagFromChartInView), handleSetRouteContext);
  yield takeEvery(getType(removeChartInView), handleSetRouteContext);
}
